import { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Prism as SyntaxHighlighter, SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import tw from 'twin.macro';
import 'styled-components/macro';

import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';

import { ReactComponent as CheckIcon } from './check.svg';
import { ReactComponent as ClipboardIcon } from './clipboard.svg';

export interface CodeBlockProps extends Omit<SyntaxHighlighterProps, 'children'> {
  children: string;
  disableCopy?: boolean;
  className?: string;
}

const CodeBlock = ({ className = '', children, disableCopy, ...syntaxHighlighterProps }: CodeBlockProps) => {
  const [copied, setCopied] = useState(false);
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  if (!children) {
    return null;
  }

  const syntaxHighlighter = (
    <SyntaxHighlighter style={vscDarkPlus} {...syntaxHighlighterProps}>
      {children}
    </SyntaxHighlighter>
  );

  if (disableCopy) {
    return syntaxHighlighter;
  }

  return (
    <CopyToClipboard
      text={children}
      options={{ debug: true }}
      onCopy={() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        trackAmplitudeEvent({ name: 'Lab - Challenge - Copied to clipboard' });
      }}
    >
      <div tw="cursor-pointer relative" className={`group ${className}`}>
        <div
          css={[
            tw`absolute bg-[#3d475b] h-4 items-center justify-center right-[-10px] rounded text-xs text-white top-[-2px] w-4`,
            copied ? tw`bg-green flex` : tw`hidden group-hover:flex`,
          ]}
        >
          {copied ? (
            <CheckIcon aria-label="Copied to clipboard!" tw="h-2 w-2" />
          ) : (
            <ClipboardIcon aria-label="Copy to clipboard" tw="h-2 w-2" />
          )}
        </div>

        {syntaxHighlighter}
      </div>
    </CopyToClipboard>
  );
};

export default CodeBlock;
